import { Mountain, Droplets, ThermometerSun, MapPin, Flag, ShoppingBag, Train } from "lucide-react";
import badRagazMap from "@/assets/bad-ragaz-map.png";
import badRagazGorge from "@/assets/bad-ragaz-gorge.png";
import badRagazAerial from "@/assets/bad-ragaz-aerial.png";
import badRagazTaminaRiver from "@/assets/bad-ragaz-tamina-river.png";
import YouTubeFacade from "./YouTubeFacade";
import badRagazSchluctEntrance from "@/assets/bad-ragaz-schlucht-entrance.png";
import badRagazTownAerial from "@/assets/bad-ragaz-town-aerial.png";
import badRagazNature from "@/assets/bad-ragaz-nature.png";
import spavillageLogo from "@/assets/bad-ragaz-spavillage-logo.png";
import { useLanguage } from "@/i18n/LanguageContext";
import { useCmsContent } from "@/hooks/useCmsContent";

const BadRagaz = () => {
  const { t } = useLanguage();
  const { get } = useCmsContent();
  const bp = t.badRagazPage;

  const highlights = [
    { icon: ThermometerSun, title: get("badragaz_tamina_therme") || bp.taminaTherme, description: get("badragaz_tamina_therme_desc") || bp.taminaThermeDesc },
    { icon: Mountain, title: get("badragaz_pizol") || bp.pizol, description: get("badragaz_pizol_desc") || bp.pizolDesc },
    { icon: Droplets, title: get("badragaz_taminaschlucht") || bp.taminaschlucht, description: get("badragaz_taminaschlucht_desc") || bp.taminaschluchtDesc },
    { icon: Flag, title: get("badragaz_golf") || bp.golf, description: get("badragaz_golf_desc") || bp.golfDesc },
    { icon: ShoppingBag, title: get("badragaz_shopping") || bp.shopping, description: get("badragaz_shopping_desc") || bp.shoppingDesc },
    { icon: Train, title: get("badragaz_transport") || bp.transport, description: get("badragaz_transport_desc") || bp.transportDesc },
  ];

  return (
    <>
      <section className="py-16 md:py-24 bg-card">
        <div className="hotel-container">
          <div className="grid lg:grid-cols-5 gap-8 lg:gap-16 items-start">
            <div className="lg:col-span-3 space-y-6">
              <div className="space-y-2">
                <p className="hotel-caption text-accent">{get("badragaz_page_region_caption") || bp.regionCaption}</p>
                <h2 className="font-display text-3xl md:text-4xl font-light text-primary leading-snug">
                  {get("badragaz_page_heading") || bp.heading}{" "}<span className="italic font-normal text-accent text-2xl md:text-3xl">{get("badragaz_page_spa") || bp.spaVillage}</span>
                </h2>
              </div>
              <div className="w-14 h-px bg-accent" />
              <p className="hotel-body text-muted-foreground">{get("badragaz_page_intro1") || bp.intro1}</p>
              <p className="hotel-body text-muted-foreground">{get("badragaz_page_intro2") || bp.intro2}</p>
              <p className="hotel-body text-muted-foreground">{get("badragaz_page_intro3") || bp.intro3}</p>
              <div className="bg-background rounded-lg p-6 shadow-soft mt-2">
                <p className="hotel-body text-muted-foreground text-sm leading-relaxed">{get("badragaz_page_access") || bp.accessInfo}</p>
              </div>
              <a href="https://heidiland.com/region/bad-ragaz/winter.html" target="_blank" rel="noopener noreferrer" className="inline-block mt-4 hover:opacity-80 transition-opacity">
                <img src={spavillageLogo} alt="Bad Ragaz Spavillage" loading="lazy" className="h-10 md:h-12 w-auto" />
              </a>
            </div>
            <div className="lg:col-span-2">
              <div className="relative sticky top-28">
                <div className="absolute -inset-3 border border-accent/30 rounded-lg" />
                <img src={badRagazMap} alt="Bad Ragaz" loading="lazy" className="w-full h-auto rounded-lg shadow-elevated relative z-10" />
                <p className="font-display text-xs text-muted-foreground italic text-center mt-4 relative z-10">{bp.mapCaption}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="py-16 md:py-20 bg-background">
        <div className="hotel-container">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <p className="hotel-caption text-accent mb-3">{get("badragaz_discover_caption") || bp.discoverCaption}</p>
            <h3 className="font-display text-2xl md:text-3xl font-light text-foreground">{get("badragaz_discover_heading") || bp.discoverHeading}</h3>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {highlights.map((item) => (
              <div key={item.title} className="group bg-card rounded-lg p-6 shadow-soft hover:shadow-medium transition-all duration-300 border border-transparent hover:border-accent/20">
                <div className="flex gap-4 items-start">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/15 transition-colors duration-300"><item.icon className="w-4 h-4 text-primary" strokeWidth={1.5} /></div>
                  <div><h4 className="font-display text-base font-semibold text-foreground mb-1">{item.title}</h4><p className="font-body text-sm text-muted-foreground leading-relaxed">{item.description}</p></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-16 md:py-20 bg-card">
        <div className="hotel-container">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <p className="hotel-caption text-accent mb-3">{bp.impressionsCaption}</p>
            <h3 className="font-display text-2xl md:text-3xl font-light text-foreground">{bp.impressionsHeading}</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 md:gap-3 auto-rows-[140px] md:auto-rows-[220px]">
            <div className="col-span-2 md:col-span-4 row-span-2 relative overflow-hidden rounded-lg group"><img src={badRagazTownAerial} alt="Bad Ragaz" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
            <div className="col-span-1 md:col-span-2 row-span-1 relative overflow-hidden rounded-lg group"><img src={badRagazSchluctEntrance} alt="Tamina Gorge" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
            <div className="col-span-1 md:col-span-2 row-span-1 relative overflow-hidden rounded-lg group"><img src={badRagazTaminaRiver} alt="Tamina" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
            <div className="col-span-1 md:col-span-3 row-span-1 relative overflow-hidden rounded-lg group"><img src={badRagazNature} alt="Nature" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
            <div className="col-span-1 md:col-span-3 row-span-1 relative overflow-hidden rounded-lg group"><img src={badRagazAerial} alt="Bad Ragaz Aerial" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
          </div>
        </div>
      </section>
      <section className="py-16 md:py-20 bg-background">
        <div className="hotel-container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <p className="hotel-caption text-accent mb-3">{bp.videoCaption}</p>
              <h3 className="font-display text-2xl md:text-3xl font-light text-foreground">{bp.videoHeading}</h3>
            </div>
            <div className="relative w-full rounded-lg overflow-hidden shadow-elevated aspect-video">
              <YouTubeFacade videoId="oPpE3vASiE4" title="Bad Ragaz" className="absolute inset-0" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BadRagaz;
